import { z } from 'zod';
import { itemTypeSchema } from './inventory';

export const salesOrderStatusSchema = z.enum(['Draft', 'Sent', 'Delivered', 'Cancelled']);

// Payment state, orthogonal to the workflow `status`. Mirrors the purchase-order
// model. A 'refunded' order is excluded from revenue reporting on the backend.
export const salesOrderPaymentStatusSchema = z.enum(['unpaid', 'paid', 'refunded']);

export type SalesOrderPaymentStatus = z.infer<typeof salesOrderPaymentStatusSchema>;

export const salesOrderItemSchema = z.object({
  inventoryItemId: z.string().min(1, 'Item is required'),
  // Set only for roasted_bulk lines — identifies the (inventoryItemId, roastType)
  // RB group from RoastedBulkGroup. Empty string for FG/GB lines.
  roastType: z.string().optional(),
  // Set only for green_bean lines that pin a specific lot. Empty string means
  // the line falls back to FIFO (default behavior). The user can override this
  // to manually select which lot drains first.
  lotId: z.string().optional(),
  lotNumber: z.string().optional(),
  processType: z.string().optional(),
  itemType: itemTypeSchema.optional(),
  name: z.string(),
  description: z.string().optional(),
  quantity: z.number()
    .min(0.001, 'Quantity must be greater than 0')
    .refine((value) => Math.round(value * 1000) / 1000 === value, 'Max 3 decimal places'),
  unitPrice: z.number().min(0, 'Unit Price must be non-negative'),
  amount: z.number().optional(),
});

const parseDate = (value?: string) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const salesOrderSchema = z.object({
  customer: z.string().min(1, 'Customer is required'),
  customerEmail: z.string().email('Invalid email address').optional().or(z.literal('')),
  emailCc: z.string().optional(),
  emailBcc: z.string().optional(),
  sendLater: z.boolean().optional(),
  orderDate: z.string().min(1, 'Order Date is required'),
  invoiceDate: z.string().optional(),
  dueDate: z.string().optional(),
  expectedShipDate: z.string().optional(),
  billingAddress: z.string().optional(),
  shippingAddress: z.string().min(1, 'Shipping address is required'),
  terms: z.string().optional(),
  shipVia: z.string().optional(),
  shippingDate: z.string().optional(),
  trackingNumber: z.string().optional(),
  businessDevelopmentExecutive: z.string().optional(),
  tags: z.string().optional(),
  store: z.string().optional(),
  status: salesOrderStatusSchema,
  notes: z.string().optional(),
  internalNotes: z.string().optional(),
  // Order-level discount. `discountType` picks how `discountValue` is read:
  // 'percent' → value is a % of the subtotal; 'amount' → value is a flat Rp
  // deduction. The discount applies to the subtotal only (not shipping).
  discountType: z.enum(['percent', 'amount']).optional(),
  discountValue: z.number().min(0).optional(),
  shippingAmount: z.number().min(0).optional(),
  // Payment tracking. Both the main form (PUT /sales-orders/:id) and the payment
  // modal (PATCH /sales-orders/:id/payment) can write paymentStatus; the payment
  // modal additionally owns paymentType/paymentProofUrl, which the form never touches.
  paymentStatus: salesOrderPaymentStatusSchema.optional(),
  paymentType: z.string().optional(),
  paymentProofUrl: z.string().nullable().optional(),
  messageOnInvoice: z.string().optional(),
  items: z.array(salesOrderItemSchema).min(1, 'At least one item is required'),
  // Onboarding: marks this order as the practice sample; stripped before display
  onboardingSample: z.boolean().optional(),
}).superRefine((data, ctx) => {
  const orderDate = parseDate(data.orderDate);
  const shippingDate = parseDate(data.shippingDate);

  if (!orderDate || !shippingDate) {
    return;
  }

  if (shippingDate < orderDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['shippingDate'],
      message: 'Actual Delivery Date must be later than or equal to Order Date'
    });
  }
});

export type SalesOrderFormData = z.infer<typeof salesOrderSchema>;

export interface SalesOrder extends SalesOrderFormData {
  id: string;
  orderNumber: string;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
}
